<template>
    <Head :title="location.name" />

    <div class="py-6">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <!-- Header with Actions -->
            <div class="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div class="mb-4 sm:mb-0">
                    <Link
                        :href="route('settings.locations.index')"
                        class="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-2"
                    >
                        <svg class="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to Locations
                    </Link>
                    <div class="flex items-center space-x-3">
                        <h1 class="text-2xl font-semibold text-gray-900 dark:text-white dark:text-gray-100">{{ location.name }}</h1>
                        <InfoButton title="Location Details & Management" variant="default">
                            <p>
                                <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                This page displays comprehensive details about a location including basic information, address, statistics,
                                child locations, assigned assets, and user access permissions. Locations can have users directly assigned
                                or inherit access from parent locations. The User Access section allows you to assign users with options
                                for inheritance to child locations.
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                • <strong>Statistics Cards:</strong> Direct Assets (at this location only), Total Assets (includes children),
                                  Child Locations count, Work Orders, and User Access counts<br>
                                • <strong>Status Badges:</strong> Shows Active/Inactive status and location code if assigned<br>
                                • <strong>Activate/Deactivate:</strong> Toggle without deleting - inactive locations hide from dropdowns<br>
                                • <strong>Edit Location:</strong> Modify name, code, type, parent, address, or description<br>
                                • <strong>Delete:</strong> Only available if no assets, work orders, or child locations exist<br>
                                • <strong>Child Locations:</strong> Click any child to navigate to its detail page<br>
                                • <strong>Assets at this Location:</strong> Shows all assets directly assigned here<br>
                                • <strong>User Access Section:</strong> View directly assigned users and inherited users (from parents)<br>
                                • <strong>Assign User:</strong> Grant user access with optional "Include child locations" for inheritance<br>
                                • <strong>Remove:</strong> Unassign directly assigned users (inherited users must be removed from parent)<br>
                                • <strong>Quick Actions:</strong> Add child location or toggle active status
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                • <strong>Direct vs Total Assets:</strong> Direct shows only this location; Total includes all child
                                  locations<br>
                                • <strong>User inheritance:</strong> Blue badges show "Inherited from Parent" - these users were assigned
                                  to a parent location with descendants enabled<br>
                                • <strong>Include descendants</strong> - When assigning users, check this to auto-grant access to all child
                                  locations<br>
                                • <strong>Duplicate assignment warning:</strong> System prevents assigning users already inherited from
                                  parents<br>
                                • <strong>Delete restrictions:</strong> Hover over disabled Delete button to see why deletion is blocked<br>
                                • <strong>Path name:</strong> Shows full hierarchy path (e.g., "Site A > Building 1 > Floor 2")<br>
                                • <strong>Add child location:</strong> Quick action link pre-fills this location as parent<br>
                                • <strong>Work Orders count:</strong> Shows maintenance activity at this location
                            </p>
                        </InfoButton>
                        <span
                            v-if="location.code"
                            class="px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                        >
                            {{ location.code }}
                        </span>
                        <span
                            :class="[
                                'px-2.5 py-1 text-xs font-medium rounded-full',
                                location.is_active
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                            ]"
                        >
                            {{ location.is_active ? 'Active' : 'Inactive' }}
                        </span>
                    </div>
                    <p v-if="location.path_name" class="mt-1 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-400">
                        {{ location.path_name }}
                    </p>
                </div>

                <div v-if="canUpdate" class="flex items-center space-x-3">
                    <button
                        @click="toggleActive"
                        class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        {{ location.is_active ? 'Deactivate' : 'Activate' }}
                    </button>
                    <Link
                        :href="route('settings.locations.edit', location.id)"
                        class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Edit Location
                    </Link>
                    <button
                        v-if="canDelete"
                        @click="showDeleteModal = true"
                        class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                        Delete
                    </button>
                    <div v-else class="relative group">
                        <button
                            disabled
                            class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-60"
                        >
                            Delete
                        </button>
                        <div class="absolute bottom-full right-0 mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                            Cannot delete: {{ deleteReasons.join(', ') }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Statistics Cards -->
            <div class="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
                <div v-if="subscriptionFeatures.asset_module" class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 rounded-md p-3">
                            <svg class="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Direct Assets</p>
                            <p class="text-2xl font-semibold text-gray-900 dark:text-white dark:text-gray-100">{{ stats.assets_count }}</p>
                        </div>
                    </div>
                </div>

                <div v-if="subscriptionFeatures.asset_module" class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-green-100 dark:bg-green-900/30 rounded-md p-3">
                            <svg class="h-6 w-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Total Assets</p>
                            <p class="text-2xl font-semibold text-gray-900 dark:text-white dark:text-gray-100">{{ stats.total_assets_count }}</p>
                        </div>
                    </div>
                </div>

                <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-purple-100 dark:bg-purple-900/30 rounded-md p-3">
                            <svg class="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Child Locations</p>
                            <p class="text-2xl font-semibold text-gray-900 dark:text-white dark:text-gray-100">{{ stats.children_count }}</p>
                        </div>
                    </div>
                </div>

                <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-orange-100 dark:bg-orange-900/30 rounded-md p-3">
                            <svg class="h-6 w-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Work Orders</p>
                            <p class="text-2xl font-semibold text-gray-900 dark:text-white dark:text-gray-100">{{ stats.work_orders_count }}</p>
                        </div>
                    </div>
                </div>

                <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-indigo-100 dark:bg-indigo-900/30 rounded-md p-3">
                            <svg class="h-6 w-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">User Access</p>
                            <p class="text-2xl font-semibold text-gray-900 dark:text-white dark:text-gray-100">{{ stats.total_users_count || 0 }}</p>
                            <p v-if="stats.inherited_users_count > 0" class="text-xs text-gray-500 dark:text-gray-400">
                                {{ stats.assigned_users_count || 0 }} direct, {{ stats.inherited_users_count }} inherited
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <!-- Main Information -->
                <div class="lg:col-span-2 space-y-6">
                    <!-- Basic Information -->
                    <div class="bg-white dark:bg-gray-800 rounded-lg shadow">
                        <div class="p-6">
                            <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Basic Information</h2>
                            <dl class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Name</dt>
                                    <dd class="mt-1 text-sm text-gray-900 dark:text-gray-100">{{ location.name }}</dd>
                                </div>
                                <div v-if="location.code">
                                    <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Code</dt>
                                    <dd class="mt-1 text-sm text-gray-900 dark:text-gray-100">{{ location.code }}</dd>
                                </div>
                                <div>
                                    <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Type</dt>
                                    <dd class="mt-1 text-sm text-gray-900 dark:text-gray-100">{{ location.type_label }}</dd>
                                </div>
                                <div v-if="location.parent">
                                    <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Parent Location</dt>
                                    <dd class="mt-1">
                                        <Link
                                            :href="route('settings.locations.show', location.parent.id)"
                                            class="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                        >
                                            {{ location.parent.name }}
                                        </Link>
                                    </dd>
                                </div>
                                <div v-if="location.description" class="sm:col-span-2">
                                    <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Description</dt>
                                    <dd class="mt-1 text-sm text-gray-900 dark:text-gray-100">{{ location.description }}</dd>
                                </div>
                            </dl>
                        </div>
                    </div>

                    <!-- Address Information -->
                    <div v-if="location.full_address" class="bg-white dark:bg-gray-800 rounded-lg shadow">
                        <div class="p-6">
                            <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Address Information</h2>
                            <div class="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-line">
                                {{ location.full_address }}
                            </div>
                        </div>
                    </div>

                    <!-- Child Locations -->
                    <div v-if="location.children && location.children.length > 0" class="bg-white dark:bg-gray-800 rounded-lg shadow">
                        <div class="p-6">
                            <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Child Locations</h2>
                            <div class="space-y-2">
                                <Link
                                    v-for="child in location.children"
                                    :key="child.id"
                                    :href="route('settings.locations.show', child.id)"
                                    class="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <div class="flex items-center space-x-3">
                                        <div :class="[
                                            'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                                            getTypeColorClass(child.type)
                                        ]">
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ child.name }}</p>
                                            <p class="text-xs text-gray-500 dark:text-gray-400">{{ getTypeLabel(child.type) }}</p>
                                        </div>
                                    </div>
                                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                </Link>
                            </div>
                        </div>
                    </div>

                    <!-- Assets at this Location -->
                    <div v-if="subscriptionFeatures.asset_module && location.assets && location.assets.length > 0" class="bg-white dark:bg-gray-800 rounded-lg shadow">
                        <div class="p-6">
                            <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Assets at this Location</h2>
                            <div class="space-y-2">
                                <Link
                                    v-for="asset in location.assets"
                                    :key="asset.id"
                                    :href="route('settings.maintenance.assets.show', asset.id)"
                                    class="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <div class="flex items-center space-x-3">
                                        <div class="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                                            <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ asset.name }}</p>
                                            <p class="text-xs text-gray-500 dark:text-gray-400">{{ asset.asset_tag || 'No tag' }}</p>
                                        </div>
                                    </div>
                                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                </Link>
                            </div>
                        </div>
                    </div>

                    <!-- Assigned Users -->
                    <div class="bg-white dark:bg-gray-800 rounded-lg shadow">
                        <div class="p-6">
                            <div class="flex items-center justify-between mb-4">
                                <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100">User Access</h2>
                                <button
                                    v-if="canUpdate && availableUsers && availableUsers.length > 0"
                                    @click="showAssignModal = true"
                                    class="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Assign User
                                </button>
                            </div>

                            <div v-if="(assignedUsers && assignedUsers.length > 0) || (inheritedUsers && inheritedUsers.length > 0)">
                                <!-- Directly Assigned Users -->
                                <div v-if="assignedUsers && assignedUsers.length > 0" class="space-y-3 mb-4">
                                    <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">Directly Assigned</h3>
                                    <div
                                        v-for="assignment in assignedUsers"
                                        :key="assignment.id"
                                        class="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-md"
                                    >
                                        <div class="flex items-center space-x-3">
                                            <div class="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                                                <svg class="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ assignment.user.name }}</p>
                                                <p class="text-xs text-gray-500 dark:text-gray-400">{{ assignment.user.email }}</p>
                                                <div class="flex items-center mt-1 space-x-2">
                                                    <span v-if="assignment.include_descendants" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                                        <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                                        </svg>
                                                        Includes Children
                                                    </span>
                                                    <span class="text-xs text-gray-500 dark:text-gray-400">
                                                        Assigned {{ new Date(assignment.assigned_at).toLocaleDateString() }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            v-if="canUpdate"
                                            @click="unassignUser(assignment.id)"
                                            class="px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>

                                <!-- Inherited Users (from parent) -->
                                <div v-if="inheritedUsers && inheritedUsers.length > 0" class="space-y-3">
                                    <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">Inherited from Parent</h3>
                                    <div
                                        v-for="inherited in inheritedUsers"
                                        :key="inherited.user_id"
                                        class="flex items-center justify-between p-3 border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 rounded-md"
                                    >
                                        <div class="flex items-center space-x-3">
                                            <div class="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                                                <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ inherited.user.name }}</p>
                                                <p class="text-xs text-gray-500 dark:text-gray-400">{{ inherited.user.email }}</p>
                                                <div class="flex items-center mt-1 space-x-2">
                                                    <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                                                        <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                                        </svg>
                                                        Via {{ inherited.inherited_from.name }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <Link
                                            :href="route('settings.locations.show', inherited.inherited_from.id)"
                                            class="px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                                        >
                                            View Parent
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <!-- Empty State -->
                            <div v-else class="text-center py-6">
                                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                                <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">No users have access to this location</p>
                                <button
                                    v-if="availableUsers && availableUsers.length > 0"
                                    @click="showAssignModal = true"
                                    class="mt-3 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                                >
                                    Assign First User
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Sidebar -->
                <div class="space-y-6">
                    <!-- Quick Actions -->
                    <div v-if="canUpdate" class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                        <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">Quick Actions</h3>
                        <div class="space-y-2">
                            <Link
                                :href="route('settings.locations.create') + '?parent_id=' + location.id"
                                class="flex items-center w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors"
                            >
                                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Add Child Location
                            </Link>
                            <button
                                @click="toggleActive"
                                class="flex items-center w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors text-left"
                            >
                                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                </svg>
                                {{ location.is_active ? 'Deactivate' : 'Activate' }}
                            </button>
                        </div>
                    </div>

                    <!-- Metadata -->
                    <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                        <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">Metadata</h3>
                        <dl class="space-y-3">
                            <div>
                                <dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Created</dt>
                                <dd class="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                    {{ new Date(location.created_at).toLocaleDateString() }}
                                </dd>
                            </div>
                            <div>
                                <dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Last Updated</dt>
                                <dd class="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                    {{ new Date(location.updated_at).toLocaleDateString() }}
                                </dd>
                            </div>
                        </dl>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Assign User Modal -->
    <Teleport to="body">
        <Transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition ease-in duration-150"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="showAssignModal"
                class="fixed inset-0 z-50 overflow-y-auto"
                aria-labelledby="modal-title"
                role="dialog"
                aria-modal="true"
            >
                <!-- Background backdrop -->
                <div
                    class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                    @click="showAssignModal = false"
                ></div>

                <!-- Modal panel -->
                <div class="flex min-h-full items-center justify-center p-4">
                    <div class="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div class="p-6">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">Assign User to Location</h3>
                    <button
                        @click="showAssignModal = false"
                        class="text-gray-400 hover:text-gray-500"
                    >
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div class="space-y-4">
                    <!-- User Selection -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-1">
                            Select User
                        </label>
                        <select
                            v-model="selectedUserId"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                            <option value="">-- Select a user --</option>
                            <option
                                v-for="user in availableUsers"
                                :key="user.id"
                                :value="user.id"
                            >
                                {{ user.name }} ({{ user.email }})
                            </option>
                        </select>
                    </div>

                    <!-- Include Descendants Checkbox -->
                    <div class="flex items-start">
                        <div class="flex items-center h-5">
                            <input
                                id="include_descendants"
                                v-model="includeDescendants"
                                type="checkbox"
                                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                            />
                        </div>
                        <div class="ml-3">
                            <label for="include_descendants" class="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                Include child locations
                            </label>
                            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                User will automatically have access to all child locations under this location
                            </p>
                        </div>
                    </div>

                    <!-- Parent Assignment Warning -->
                    <div v-if="parentWarning" class="rounded-md bg-red-50 dark:bg-red-900/20 p-3 border border-red-200 dark:border-red-800">
                        <div class="flex">
                            <svg class="h-5 w-5 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div class="ml-3">
                                <h3 class="text-sm font-medium text-red-800 dark:text-red-300">
                                    Cannot assign user
                                </h3>
                                <p class="mt-1 text-sm text-red-700 dark:text-red-400">
                                    {{ parentWarning }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-6 flex justify-end space-x-3">
                    <button
                        @click="showAssignModal = false"
                        class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                        Cancel
                    </button>
                    <button
                        @click="assignUser"
                        :disabled="!selectedUserId || assigningUser || parentWarning || checkingParent"
                        class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <span v-if="assigningUser">Assigning...</span>
                        <span v-else-if="checkingParent">Checking...</span>
                        <span v-else>Assign User</span>
                    </button>
                </div>
            </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>

    <!-- Delete Confirmation Modal -->
    <div
        v-if="showDeleteModal"
        class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50"
        @click.self="showDeleteModal = false"
    >
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div class="p-6">
                <div class="flex items-center mb-4">
                    <div class="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                        <svg class="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <div class="ml-4">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">Delete Location</h3>
                    </div>
                </div>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
                    Are you sure you want to delete "{{ location.name }}"? This action cannot be undone.
                </p>
                <div class="flex justify-end space-x-3">
                    <button
                        @click="showDeleteModal = false"
                        class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Cancel
                    </button>
                    <button
                        @click="deleteLocation"
                        :disabled="deletingLocation"
                        class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <span v-if="deletingLocation">Deleting...</span>
                        <span v-else>Delete</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, Transition, Teleport } from 'vue';
import { Head, Link, router, usePage } from '@inertiajs/vue3';
import { useSecureAxios } from '@/Composables/useSecureAxios';
import route from '@/Utils/route';
import axios from 'axios';
import InfoButton from '@/Components/Common/InfoButton.vue';

// Get secure axios methods
const { post } = useSecureAxios();

// Access subscription features from Inertia shared props
const page = usePage();
const subscriptionFeatures = computed(() => page.props.subscriptionFeatures || { asset_module: false, equipment_module: false });

// Check permissions
const permissions = computed(() => page.props.auth?.permissions || []);
const canUpdate = computed(() => permissions.value.includes('system.settings.update'));

const props = defineProps({
    location: Object,
    stats: Object,
    canDelete: Boolean,
    deleteReasons: Array,
    assignedUsers: Array,
    inheritedUsers: Array,
    availableUsers: Array,
});

const showDeleteModal = ref(false);
const deletingLocation = ref(false);
const showAssignModal = ref(false);
const selectedUserId = ref('');
const includeDescendants = ref(false);
const parentWarning = ref('');
const assigningUser = ref(false);
const checkingParent = ref(false);

// Watch for user selection to check parent assignment
watch(selectedUserId, async (newUserId) => {
    if (!newUserId) {
        parentWarning.value = '';
        return;
    }

    checkingParent.value = true;
    try {
        const response = await post(route('settings.locations.checkParentAssignment'), {
            user_id: newUserId,
            location_id: props.location.id,
        });

        if (response.data.hasParentAccess) {
            parentWarning.value = response.data.message;
        } else {
            parentWarning.value = '';
        }
    } catch (error) {
        console.error('Error checking parent assignment:', error);
        parentWarning.value = '';
    } finally {
        checkingParent.value = false;
    }
});

const toggleActive = () => {
    router.post(route('settings.locations.toggleActive', props.location.id), {}, {
        preserveScroll: true,
    });
};

const deleteLocation = () => {
    deletingLocation.value = true;
    router.delete(route('settings.locations.destroy', props.location.id), {
        onFinish: () => {
            deletingLocation.value = false;
            showDeleteModal.value = false;
        },
    });
};

const assignUser = () => {
    if (!selectedUserId.value) return;

    // Prevent assignment if user already has access via parent
    if (parentWarning.value) {
        return;
    }

    assigningUser.value = true;
    router.post(
        route('settings.locations.assignUser', props.location.id),
        {
            user_id: selectedUserId.value,
            include_descendants: includeDescendants.value,
        },
        {
            preserveScroll: true,
            preserveState: false,
            onSuccess: () => {
                showAssignModal.value = false;
                selectedUserId.value = '';
                includeDescendants.value = false;
                parentWarning.value = '';
            },
            onFinish: () => {
                assigningUser.value = false;
            },
        }
    );
};

const unassignUser = (assignmentId) => {
    if (!confirm('Are you sure you want to remove this user assignment?')) {
        return;
    }

    router.delete(
        route('settings.locations.unassignUser', props.location.id, assignmentId),
        {
            preserveScroll: true,
            preserveState: false,
        }
    );
};

const getTypeLabel = (type) => {
    const labels = {
        building: 'Building',
        floor: 'Floor',
        room: 'Room',
        site: 'Site',
        warehouse: 'Warehouse',
        other: 'Other',
    };
    return labels[type] || type;
};

const getTypeColorClass = (type) => {
    const colors = {
        building: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
        floor: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
        room: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
        site: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
        warehouse: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
        other: 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400',
    };
    return colors[type] || colors.other;
};
</script>
